// ===== consumable-pricing.service.ts ============================================
import { Injectable } from '@angular/core';
import { ConsumableLine, ConsumablePricingContext, ConsumablePricingResult } from './consumable-models';
import { ConsumableRule } from './consumable-rule-base';
import { RulePartialPackDispensing } from './rules/rule-partial-pack-dispensing';
import { RuleFullPackPricing } from './rules/rule-full-pack-pricing';
import { RuleMaterialVialPricing } from './rules/rule-material-vial-pricing';
import { RuleVolumeBasedPricing } from './rules/rule-volume-based-pricing';

@Injectable({ providedIn: 'root' })
export class ConsumablePricingService {
    private rules: ConsumableRule[] = [];
    
    constructor() {
        // Register all pricing rules in priority order
        this.rules = [
            new RuleVolumeBasedPricing(), // Highest priority for volume-based materials
            new RulePartialPackDispensing(),
            new RuleFullPackPricing(),
            new RuleMaterialVialPricing()
        ];
        
        // Sort by priority (lower numbers first)
        this.rules.sort((a, b) => a.priority - b.priority);
    }
    
    /**
     * Calculate pricing for a consumable line item
     */
    calculatePrice(
        line: ConsumableLine,
        ctx: ConsumablePricingContext
    ): ConsumablePricingResult {
        // Find the first applicable rule (rules are sorted by priority)
        const applicableRule = this.rules.find(rule => rule.applies(line, ctx));
        
        if (!applicableRule) {
            // Fallback: use basic calculation
            return this.fallbackCalculation(line, ctx);
        }
        
        // Apply the highest priority applicable rule
        const result = applicableRule.apply(line, ctx);
        
        return result;
    }
    
    /**
     * Fallback calculation when no rules apply
     * Note: VAT is applied separately after pricing calculations
     */
    private fallbackCalculation(
        line: ConsumableLine,
        ctx: ConsumablePricingContext
    ): ConsumablePricingResult {
        const basePrice = line.unitPrice || line.singleExitPrice || 0;
        let finalPrice = basePrice;
        
        // Apply doctor markup if not a network doctor (VAT is applied separately)
        if (!ctx.isNetworkDoctor && ctx.modifier) {
            const multiplier = ctx.modifier / 100;
            finalPrice *= multiplier;
        }
        
        const quantity = line.quantity || 1;
        const totalPrice = finalPrice * quantity;
        
        return {
            line: { ...line },
            calculatedUnitPrice: finalPrice,
            calculatedTotalPrice: totalPrice,
            trace: ['Fallback calculation applied (VAT will be applied separately)'],
            warnings: ['No specific pricing rule matched this consumable']
        };
    }
    
    /**
     * Get all applicable rules for a consumable line (for debugging/UI display)
     */
    getApplicableRules(line: ConsumableLine, ctx: ConsumablePricingContext): ConsumableRule[] {
        return this.rules.filter(rule => rule.applies(line, ctx));
    }
}

